import { Box, Divider, Typography } from "@mui/material";
import { muiTheme } from "@/styles/theme";
import { useOrganization } from "@/modules/common/context/organizations/get-user-organizations.provider";
import { BriefcaseBusiness } from "lucide-react";
import ResponsiveEditDialog from "@/modules/common/components/modals/responsive-edit-dialog";
import OrganizationDropdown from "@/modules/common/components/organizations/organization-switch";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import AccountManagersDropdown from "@/modules/common/components/organizations/account-managers-dropdown";
import { FilterType } from "@/modules/common/constants";
import BecomeFranchiseToggle from "@/modules/common/components/organizations/become-franchise-toggle";
import {
  OrganizationTierTypes,
  PermissionLevel,
  PermissionsEnum,
} from "@/modules/common/types";
import PermissionGuard from "@/modules/common/components/wrappers/permission-guard";

interface OrganisationsActionsMobileProps {
  filterType: FilterType;
  setFilterType: React.Dispatch<React.SetStateAction<FilterType>>;
  isMultiple?: boolean;
  cancelButton?: JSX.Element;
  applyButton?: JSX.Element;
  openOrganizationsModal: boolean;
  setOpenOrganizationsModal: (value: boolean) => void;
  isBecomeFranchiseOnly: boolean;
  setIsBecomeFranchiseOnly: (value: boolean) => void;
}

const OrganisationsActionsMobile: React.FC<OrganisationsActionsMobileProps> = ({
  filterType,
  setFilterType,
  isMultiple,
  cancelButton,
  applyButton,
  openOrganizationsModal,
  setOpenOrganizationsModal,
  isBecomeFranchiseOnly,
  setIsBecomeFranchiseOnly,
}) => {
  const {
    selectedOrganizations,
    selectedSingleOrganization,
    selectedManagers,
    managedOrganizations,
  } = useOrganization();

  const screen = useScreenSize();
  const getSelectedManagersLabel = () => {
    if (selectedManagers.length === 1) {
      return selectedManagers[0].name;
    }
    if (selectedManagers.length > 1) {
      return `${selectedManagers[0].name} + ${selectedManagers.length - 1}`;
    }
    return "Not Set";
  };

  const getSelectedOrganizationsLabel = () => {
    if (isMultiple && !isBecomeFranchiseOnly) {
      if (selectedOrganizations?.length === 1) {
        return selectedOrganizations[0].name;
      }
      if (selectedOrganizations?.length && selectedOrganizations.length > 1) {
        return `${selectedOrganizations[0]?.name} + ${
          selectedOrganizations?.length - 1
        }`;
      }
      return "Not Set";
    }
    return selectedSingleOrganization?.name || "Not Set";
  };

  return (
    <>
      <Box
        onClick={() => setOpenOrganizationsModal(true)}
        sx={{
          cursor: "pointer",
          display: "flex",
          backgroundColor: muiTheme.colorSchemes.light.palette.primary[50],
          gap: "4px",
          padding: "4px 8px",
          borderRadius: "4px",
          alignItems: "center",
        }}
      >
        <BriefcaseBusiness
          width="20px"
          height="20px"
          color={muiTheme.colorSchemes.light.palette.primary[500]}
        />

        <Divider
          orientation="vertical"
          sx={{
            width: "1px",
            height: "16px",
            backgroundColor: `${muiTheme.colorSchemes.light.palette.primary[200]}`,
          }}
        />
        <PermissionGuard
          permissions={[
            {
              name: PermissionsEnum.CROSS_ACCOUNTMANAGERS_SELECTION,
              level: PermissionLevel.READ,
            },
          ]}
        >
          {isMultiple && (
            <Typography
              variant="h5_med"
              sx={{
                color: muiTheme.colorSchemes.light.palette.primary[500],
              }}
            >
              {screen.isMobile ? "Acc.Mngrs:" : "Account Managers:"}{" "}
              {getSelectedManagersLabel()}
            </Typography>
          )}
        </PermissionGuard>

        <Divider
          orientation="vertical"
          sx={{
            width: "1px",
            height: "16px",
            backgroundColor: `${muiTheme.colorSchemes.light.palette.primary[200]}`,
          }}
        />
        <Typography
          variant="h5_med"
          sx={{
            color: muiTheme.colorSchemes.light.palette.primary[500],
          }}
        >
          {screen.isMobile ? "Org:" : "Organization:"}
          {getSelectedOrganizationsLabel()}
        </Typography>
      </Box>
      {openOrganizationsModal && (
        <ResponsiveEditDialog
          open={openOrganizationsModal}
          setOpen={setOpenOrganizationsModal}
          header="Organization Filters"
          cancelButton={cancelButton}
          applyButton={applyButton}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              width: "100%",
            }}
          >
            <PermissionGuard
              permissions={[
                {
                  name: PermissionsEnum.CROSS_ACCOUNTMANAGERS_SELECTION,
                  level: PermissionLevel.READ,
                },
              ]}
            >
              {isMultiple && (
                <AccountManagersDropdown
                  filterType={filterType}
                  setFilterType={setFilterType}
                  isBecomeFranchiseOnly={isBecomeFranchiseOnly}
                />
              )}
            </PermissionGuard>
            <OrganizationDropdown
              filterType={filterType}
              setFilterType={setFilterType}
              isMultiple={isMultiple}
              isBecomeFranchiseOnly={isBecomeFranchiseOnly}
            />
            {isMultiple &&
              managedOrganizations?.some(
                (org) =>
                  org.organization?.tier?.name ===
                  OrganizationTierTypes.FRANCHISE
              ) && (
                <Box sx={{ display: "flex", alignSelf: "left" }}>
                  <BecomeFranchiseToggle
                    isBecomeFranchiseOnly={isBecomeFranchiseOnly}
                    setIsBecomeFranchiseOnly={setIsBecomeFranchiseOnly}
                  />
                </Box>
              )}
          </Box>
        </ResponsiveEditDialog>
      )}
    </>
  );
};

export default OrganisationsActionsMobile;
